// hooks/useBackgroundUploadSpeedTest.ts
import { useEffect, useState } from 'react';
import { measureAverageUploadSpeed } from '../../pages/VideoToolPage/utils';
import { getSpeedTestSignal } from '../../services/upload/speedTestController';
import { useTrackEvent } from './useTrackEvent';
import { EventNames } from '../constants/constants';
import { msSinceArrival } from '../../services/analytics/telemetry';

/**
 * NetworkInformation API — partial typing; Safari/iOS don't ship this so all
 * callers must null-check `navigator.connection`. We only consult `saveData`
 * here, but keep the other fields documented for future use.
 */
interface NetworkInformationLike {
  effectiveType?: 'slow-2g' | '2g' | '3g' | '4g';
  saveData?: boolean;
  downlink?: number;
}

const getConnection = (): NetworkInformationLike | undefined => {
  if (typeof navigator === 'undefined') return undefined;
  return (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
};

export function useBackgroundUploadSpeedTest({
  sizeInBytes = 1 * 1024 * 1024,
  rounds = 3,
  autoStart = true,
}: {
  sizeInBytes?: number;
  rounds?: number;
  autoStart?: boolean;
}) {
  const [speedMbps, setSpeedMbps] = useState<number | null>(null);
  const [uploadTime, setUploadTime] = useState<number | null>(null);
  const [loading, setLoading] = useState(autoStart);
  const [error, setError] = useState<string | null>(null);
  const { trackEvent } = useTrackEvent();

  useEffect(() => {
    if (!autoStart) return;

    // Respect data-saver mode — the user has explicitly asked the browser
    // to conserve data; we don't run a synthetic upload probe in that case.
    // Upload.start() falls back to MINCHUNKSIZE without a cached value.
    //
    // Previously we also short-circuited on `effectiveType === '4g'` and
    // cached a synthetic 8 Mbps seed to avoid burning ~3 MB of upstream on
    // known-fast connections. That seed got displayed in the home-screen
    // "Internet Speed" badge as if it were a real measurement, which made
    // the badge always show 8 Mbps for any Chromium user on Wi-Fi. We now
    // run the real test on every browser so the displayed number reflects
    // actual conditions.
    const conn = getConnection();
    if (conn?.saveData) {
      setLoading(false);
      trackEvent({ action: EventNames.landing_speed_unavailable, reason: 'save_data', rounds_attempted: 0 });
      return;
    }

    let cancelled = false;

    const runTest = async () => {
      try {
        const result = await measureAverageUploadSpeed(sizeInBytes, rounds, undefined, trackEvent, getSpeedTestSignal());
        if (!cancelled) {
          setSpeedMbps(result.speedMbps);
          setUploadTime(result.uploadTimeSeconds);
        }
        trackEvent({
          action: EventNames.landing_speed_ready,
          mbps: result.speedMbps,
          rounds: result.rounds,
          t_since_arrival_ms: msSinceArrival(),
        });
      } catch (err) {
        // AbortError = the real upload took the pipe; not a user-facing error.
        const aborted = (err as { name?: string } | null)?.name === 'AbortError';
        trackEvent({
          action: EventNames.landing_speed_unavailable,
          reason: aborted ? 'aborted_upload_started' : 'error',
          rounds_attempted: rounds,
        });
        if (aborted) return;
        if (!cancelled) {
          setError((err as Error).message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    runTest();

    return () => {
      cancelled = true;
    };
  }, [autoStart, sizeInBytes, rounds]);

  return {
    speedMbps,
    uploadTime,
    loading,
    error,
  };
}
